import { useState } from "react";
import { Header } from "../components/promill/Header";
import { Footer } from "../components/promill/Footer";
import { toast } from "sonner";
import { Mail, Phone, MessageCircle, MapPin, Send } from "lucide-react";
import { WHATSAPP_URL } from "../data/promill";

const ContactPage = () => {
    const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
    const [sent, setSent] = useState(false);
    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.email || !form.message) {
            toast.error("Please fill your name, email and message.");
            return;
        }
        try {
            const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(form),
            });
            if (!res.ok) throw new Error("Network");
            setSent(true);
            toast.success("Thanks! We'll be in touch within 24 hours.");
            setForm({ name: "", email: "", phone: "", message: "" });
        } catch (_e) {
            toast.error("Something went wrong. Please try WhatsApp instead.");
        }
    };

    return (
        <div data-testid="contact-page" className="bg-pm-cream min-h-screen">
            <Header />

            <section className="pt-10 md:pt-16 pb-8">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <span className="inline-block text-pm-orange font-bold tracking-[0.25em] uppercase text-[11px] mb-3">
                        Get In Touch
                    </span>
                    <h1 className="text-4xl md:text-6xl font-extrabold tracking-[-0.02em] text-pm-black leading-tight">
                        Say hello to <span className="text-pm-green">ProMill.</span>
                    </h1>
                    <p className="mt-3 text-pm-black/65 max-w-2xl">
                        Enquiries, partnerships, feedback or just love letters — drop us a line.
                        We reply within 24 hours.
                    </p>
                </div>
            </section>

            <section className="py-8 pb-20">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-3 gap-8">
                    {/* Contact info */}
                    <aside className="space-y-4">
                        <a href="mailto:hello@promill.in" data-testid="contact-email" className="flex gap-4 items-start bg-white p-5 rounded-2xl border border-pm-black/8 hover:border-pm-green transition-colors">
                            <div className="h-11 w-11 rounded-xl bg-pm-yellow/20 flex items-center justify-center shrink-0">
                                <Mail className="w-5 h-5 text-pm-green" />
                            </div>
                            <div>
                                <div className="text-xs uppercase tracking-widest text-pm-black/50 font-bold">Email</div>
                                <div className="mt-1 font-bold text-pm-black">hello@promill.in</div>
                            </div>
                        </a>
                        <a href="tel:+919517717917" data-testid="contact-phone" className="flex gap-4 items-start bg-white p-5 rounded-2xl border border-pm-black/8 hover:border-pm-green transition-colors">
                            <div className="h-11 w-11 rounded-xl bg-pm-yellow/20 flex items-center justify-center shrink-0">
                                <Phone className="w-5 h-5 text-pm-green" />
                            </div>
                            <div>
                                <div className="text-xs uppercase tracking-widest text-pm-black/50 font-bold">Phone</div>
                                <div className="mt-1 font-bold text-pm-black">+91 95177 17917</div>
                            </div>
                        </a>
                        <a href={WHATSAPP_URL} target="_blank" rel="noreferrer" data-testid="contact-whatsapp" className="flex gap-4 items-start bg-pm-green p-5 rounded-2xl text-white hover:bg-pm-black transition-colors">
                            <div className="h-11 w-11 rounded-xl bg-pm-yellow flex items-center justify-center shrink-0">
                                <MessageCircle className="w-5 h-5 text-pm-green" />
                            </div>
                            <div>
                                <div className="text-xs uppercase tracking-widest text-pm-yellow font-bold">WhatsApp</div>
                                <div className="mt-1 font-bold">Chat with us now</div>
                            </div>
                        </a>
                        <div className="flex gap-4 items-start bg-white p-5 rounded-2xl border border-pm-black/8">
                            <div className="h-11 w-11 rounded-xl bg-pm-yellow/20 flex items-center justify-center shrink-0">
                                <MapPin className="w-5 h-5 text-pm-green" />
                            </div>
                            <div>
                                <div className="text-xs uppercase tracking-widest text-pm-black/50 font-bold">HQ</div>
                                <div className="mt-1 font-bold text-pm-black">Bengaluru, India</div>
                                <div className="text-sm text-pm-black/60">Stores across India — coming soon</div>
                            </div>
                        </div>
                    </aside>

                    {/* Form */}
                    <form onSubmit={submit} data-testid="contact-form" className="lg:col-span-2 bg-white rounded-2xl border border-pm-black/8 p-6 md:p-8 space-y-5">
                        <div className="grid sm:grid-cols-2 gap-5">
                            <label className="block">
                                <span className="text-xs uppercase tracking-widest font-bold text-pm-black/60">Name *</span>
                                <input value={form.name} onChange={set("name")} required data-testid="contact-name" className="mt-2 w-full h-12 px-4 rounded-xl border border-pm-black/10 focus:outline-none focus:border-pm-green focus:ring-2 focus:ring-pm-yellow/30" />
                            </label>
                            <label className="block">
                                <span className="text-xs uppercase tracking-widest font-bold text-pm-black/60">Email *</span>
                                <input type="email" value={form.email} onChange={set("email")} required data-testid="contact-email-input" className="mt-2 w-full h-12 px-4 rounded-xl border border-pm-black/10 focus:outline-none focus:border-pm-green focus:ring-2 focus:ring-pm-yellow/30" />
                            </label>
                        </div>
                        <label className="block">
                            <span className="text-xs uppercase tracking-widest font-bold text-pm-black/60">Phone (optional)</span>
                            <input value={form.phone} onChange={set("phone")} data-testid="contact-phone-input" className="mt-2 w-full h-12 px-4 rounded-xl border border-pm-black/10 focus:outline-none focus:border-pm-green focus:ring-2 focus:ring-pm-yellow/30" />
                        </label>
                        <label className="block">
                            <span className="text-xs uppercase tracking-widest font-bold text-pm-black/60">Message *</span>
                            <textarea rows={5} value={form.message} onChange={set("message")} required data-testid="contact-message" className="mt-2 w-full px-4 py-3 rounded-xl border border-pm-black/10 focus:outline-none focus:border-pm-green focus:ring-2 focus:ring-pm-yellow/30" />
                        </label>
                        <button type="submit" data-testid="contact-submit" className="inline-flex items-center gap-2 h-12 px-7 rounded-full bg-pm-green text-pm-yellow font-bold hover:bg-pm-black transition-colors">
                            Send Message
                            <Send className="w-4 h-4" />
                        </button>
                        {sent && <div data-testid="contact-success" className="text-sm text-pm-green font-bold">Message received. We'll reply soon.</div>}
                    </form>
                </div>
            </section>

            <Footer />
        </div>
    );
};

export default ContactPage;
